import Vue from 'vue';
import Component from 'vue-class-component';
import { Emit, Prop } from 'vue-property-decorator';
import Moment from 'moment';
import { cloneDeep } from 'lodash';
import { ScheduleBlock, ScheduleBlockItem } from '@/tools/models/class-builder.models';
import { WEEK_DAYS } from '@/values/general.values';
import { EdGenericCalendarWeekdays } from '@/core/components/ed-generic-calendar/interfaces/ed-generic-calendar-weekdays.interface';

@Component({
  name: 'ed-block-item-add',
  components: {
    VueTimepicker: () => import(/* "VueTimepicker" */ 'vue2-timepicker/src/vue-timepicker.vue'),
  }
})
export default class EdBlockItemAddComponent extends Vue {
  @Prop({ default: '' }) readonly buttonLabel: string;
  @Prop({ default: () => [] }) readonly blocks: ScheduleBlockItem[];
  @Prop({ default: () => [] }) readonly currentEntriesCreated: ScheduleBlockItem[];
  @Prop() readonly scheduleBlocksCreated: ScheduleBlock[];
  @Prop() readonly scheduleBlockInEdition: ScheduleBlock;
  @Prop() readonly weekdays: number[] | EdGenericCalendarWeekdays[];
  @Prop() readonly scheduleStartHour: string; 
  @Prop() readonly scheduleEndHour: string;
  @Prop() readonly isWeeklySchedule: boolean;

  isEditingBlockGroup = false;
  isCurrentBlockWithConflict = false;
  backupBlocksOnEdit: ScheduleBlockItem[] = [];
  currentDaysSelected = [];
  currentStartHour = { hh: '08', mm: '00', A: 'AM', };
  currentEndHour = { hh: '09', mm: '00', A: 'AM', };
  hoursRange = [];
  toHoursRange = [];
  endMinutesRange = [['0', '59']];
  skipUpdateEndHour = false;
  LONG_DURATION = 64;
  SHORT_DURATION = 48;


  get allBlocks(): ScheduleBlockItem[] {
    const entries = [];
    const currentEntriesCreated = this.currentEntriesCreated.filter(
      e => !e.toDelete && !this.blocks.find(b => b.id === e.id)
    );
    const blocks: ScheduleBlock[] = this.scheduleBlocksCreated;
    const blockToEdit: ScheduleBlock = this.scheduleBlockInEdition;
    
    blocks.forEach((block) => {
      if (blockToEdit?.id) {
        if (blockToEdit.id !== block.id) {
          block.scheduleEntries.forEach((entry) => {
            entries.push(entry);
          });
        }
      } else {
        block.scheduleEntries.forEach((entry) => {
          entries.push(entry);
        });
      }
    });

    return [...entries, ...currentEntriesCreated];
  }

  get daysOfWeek(): any[] {
    let days = [];
    if (this.isWeeklySchedule) {
      days = this.weekdays.map(day => { 
        return {
          name: this.getCapitalizeLabel(WEEK_DAYS[day]), 
          id: WEEK_DAYS[day].toUpperCase()
        }});
    } else {
    let weekdays = this.weekdays as EdGenericCalendarWeekdays[];
    days = weekdays.map(day => { 
      return {
        name: this.$t(`tools.classBuilder.${day.key}`) as string, 
        id: day.key
      }});
    }
    return days;
  }

  get isDoneAddTimeValid() {
    const hasDaysSelected = this.currentDaysSelected.length > 0;
    const hasDifferentStartEndHour = this.currentStartHour !== this.currentEndHour;

    return hasDaysSelected && 
      hasDifferentStartEndHour && 
      !this.isEndHourGreaterThanStartHour && 
      !this.isCurrentBlockWithConflict;
  }

  get isEndHourGreaterThanStartHour(): boolean {
    const start = Moment(`${this.currentStartHour.hh}:${this.currentStartHour.mm} ${this.currentStartHour.A}`, 'hh:mm A');
    const end = Moment(`${this.currentEndHour.hh}:${this.currentEndHour.mm} ${this.currentEndHour.A}`, 'hh:mm A');

    return end.isBefore(start) || end.isSame(start);
  }

  @Emit('save')
  emitOnSave(add: ScheduleBlockItem[] = [], remove: ScheduleBlockItem[] = []) {
    return {
      add,
      remove,
    };
  }

  @Emit('cancel')
  emitOnCancel() {
    this.resetForm()
    return;
  }

  mounted() {
    this.init();

    if (this.blocks?.length > 0) {
      this.loadBlocksToEdit(this.blocks);
    }
  }

  init() {
    this.setRangeHoursAvailable();
    this.setDefaultHours();
  }

  setRangeHoursAvailable() {
    const startHour = this.scheduleStartHour;
    const endHour = this.scheduleEndHour;
    const startMoment = Moment(startHour, 'HH:mm');
    const endMoment = Moment(endHour, 'HH:mm');
    const hours = [];

    while (!startMoment.isAfter(endMoment)) {
      hours.push(startMoment.format('ha').replace('m', ''));

      startMoment.add(1, 'hours');
    }
    this.hoursRange = hours;
  }

  setDefaultHours() {
    const startHour = this.scheduleStartHour;
    const startMoment = Moment(startHour, 'HH:mm');
    const endMoment = Moment(startHour, 'HH:mm').add(this.LONG_DURATION, 'minutes');
    this.skipUpdateEndHour = true;  //skip auto update while setting default hours
    this.currentStartHour = {
      hh: startMoment.format('hh'),
      mm: startMoment.format('mm'),
      A: startMoment.format('A'),
    };
    this.currentEndHour = {
      hh: endMoment.format('hh'),
      mm: endMoment.format('mm'),
      A: endMoment.format('A'),
    };
  }

  onEndHourChanged() {
    const endHour = Moment(`${this.currentEndHour.hh}:${this.currentEndHour.mm} ${this.currentEndHour.A}`, 'hh:mm A');
    const scheduleEndHour = Moment(this.scheduleEndHour, 'HH:mm');

    if (endHour.isSame(scheduleEndHour)) {
      this.endMinutesRange = [['00']];
    } else if (endHour.isAfter(scheduleEndHour)) {
      this.currentEndHour = {
        hh: scheduleEndHour.format('hh'),
        mm: scheduleEndHour.format('mm'),
        A: scheduleEndHour.format('A'),
      };
    } else {
      this.endMinutesRange = [['00', '59']];
    } 

    this.onFormChanged();
  }

  onStartHourChanged() {
    if(this.skipUpdateEndHour){
      this.skipUpdateEndHour = false;
      return;
    }
      
    const startHour = Moment(`${this.currentStartHour.hh}:${this.currentStartHour.mm} ${this.currentStartHour.A}`, 'hh:mm A');
    let blockDuration = this.LONG_DURATION;
    if(!this.isWeeklySchedule){
      const blocks = this.getCurrentBlocksItem();
      blockDuration = this.LONG_DURATION; 
      if (blocks.some(b => b.day === 'A' || b.day === 'B')) {
        blockDuration = this.LONG_DURATION;
      } else if(blocks.length > 0){
        blockDuration = this.SHORT_DURATION;
      }
    }
    startHour.add(blockDuration, 'minutes');
    
    this.currentEndHour = {
        hh: startHour.format('hh'),
        mm: startHour.format('mm'),
        A: startHour.format('A'),
      };
    this.onFormChanged();
  }

  onFormChanged() {
    const blocks = this.getCurrentBlocksItem();

    this.isCurrentBlockWithConflict = blocks.some(
      (b) => !this.canAddItem(this.allBlocks, b)
    );
  }

  canAddItem(items: ScheduleBlockItem[], newItem: ScheduleBlockItem): boolean {
    return !items.some((item) => {
      if (item.day === newItem.day && item.start === newItem.start && item.end === newItem.end) {
        return true;
      }
      
      const itemStart = Moment(item.start, 'HH:mm');
      const itemEnd = Moment(item.end, 'HH:mm');
      const newItemStart = Moment(newItem.start, 'HH:mm');
      const newItemEnd = Moment(newItem.end, 'HH:mm');

      const isTheDay = item.day === newItem.day;
      const isNewStartBetween = newItemStart.isBetween(itemStart, itemEnd, undefined, '()');
      const isNewEndBetween = newItemEnd.isBetween(itemStart, itemEnd, undefined, '()');
      const isEndBetween = itemEnd.isBetween(newItemStart, newItemEnd, undefined, '()');
      const isStartBetween = itemStart.isBetween(newItemStart, newItemEnd, undefined, '()');

      if (isTheDay && (isNewStartBetween || isNewEndBetween || isEndBetween || isStartBetween)) {
        return true;
      }

      return false;
    });
  }

  onSaveBlock() {
    if (this.isEditingBlockGroup) {
      this.updateBlocks();
      
      return;
    }

    const blocksToAdd: ScheduleBlockItem[] = this.getCurrentBlocksItem();

    this.emitOnSave(blocksToAdd);
    this.resetForm();
  }

  getCurrentBlocksItem() {
    return this.currentDaysSelected.map((dayValue) => {
      const day = cloneDeep(dayValue);
      const start = Moment(`${this.currentStartHour.hh}:${this.currentStartHour.mm} ${this.currentStartHour.A}`, 'hh:mm A').format('HH:mm');
      const end = Moment(`${this.currentEndHour.hh}:${this.currentEndHour.mm} ${this.currentEndHour.A}`, 'hh:mm A').format('HH:mm');

      return { day, start, end, }
    });
  }

  getCurrentBlocksItemsToUpdate() {
    const currentBlocksItemsUpdated = [];
    const start = Moment(`${this.currentStartHour.hh}:${this.currentStartHour.mm} ${this.currentStartHour.A}`, 'hh:mm A').format('HH:mm');
    const end = Moment(`${this.currentEndHour.hh}:${this.currentEndHour.mm} ${this.currentEndHour.A}`, 'hh:mm A').format('HH:mm');

    this.currentDaysSelected.forEach((dayValue) => {
      const day = cloneDeep(dayValue);
      const blockFound = this.backupBlocksOnEdit.find(b => (b.day === day));

      if (blockFound) {
        currentBlocksItemsUpdated.push({
          ...blockFound,
          start,
          end,
        });
      } else {
        currentBlocksItemsUpdated.push({ day, start, end });
      }
    });

    this.backupBlocksOnEdit.forEach((block) => {
      if (!currentBlocksItemsUpdated.find(b => b.id === block.id)) {
        currentBlocksItemsUpdated.push({ ...block, toDelete: true });
      }
    });

    return currentBlocksItemsUpdated;
  }

  updateBlocks() {
    const blocksToUpdate = this.getCurrentBlocksItemsToUpdate();

    this.emitOnSave(blocksToUpdate);
    this.resetForm();
  }

  resetForm() {
    this.isEditingBlockGroup = false;
    this.currentDaysSelected = [];
    this.setDefaultHours();
    this.isCurrentBlockWithConflict = false;
  }

  loadBlocksToEdit(blocksToEdit: ScheduleBlockItem[]) {
    this.backupBlocksOnEdit = cloneDeep(blocksToEdit);
    this.isEditingBlockGroup = true;
    this.currentDaysSelected = blocksToEdit.filter(d => !d.toDelete).map((b) =>  b.day );
    this.skipUpdateEndHour = true;  //skip auto update while setting current hours
    this.currentStartHour = {
      hh: Moment(`2022-03-01 ${blocksToEdit[0].start}`).format('hh'),
      mm: Moment(`2022-03-01 ${blocksToEdit[0].start}`).format('mm'),
      A:  Moment(`2022-03-01 ${blocksToEdit[0].start}`).format('A'),
    };
    this.currentEndHour = {
      hh: Moment(`2022-03-01 ${blocksToEdit[0].end}`).format('hh'),
      mm: Moment(`2022-03-01 ${blocksToEdit[0].end}`).format('mm'),
      A:  Moment(`2022-03-01 ${blocksToEdit[0].end}`).format('A'),
    };
  }

  getCapitalizeLabel(label: string): string {
    if (label?.length === 1) {
      return label.toUpperCase();
    }

    return label.charAt(0).toUpperCase() + label.slice(1).toLocaleLowerCase();
  }
}